import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { ProjectStageRaw } from 'src/types';
import { META_DATA_QUERY_FIELDS } from './metadata.fragments';

const GET_PROJECT_STAGE_QUERY = gql`
  query GetProjectStage($params: QueryParams, $skipTotalRecords: Boolean = false)
  @entity(name: "ProjectStage") {
    getProjectStage(queryParams: $params) {
      records {
        id
        projectStageId
        name
        createdAt
        updatedAt
      }
      metadata {
        ...MetaDataQueryFields
      }
    }
  }
  ${META_DATA_QUERY_FIELDS}
`;

type GetProjectStages = {
  getProjectStage: Records<ProjectStageRaw>;
};

export type ProjectStage = Pick<
  ProjectStageRaw,
  'id' | 'projectStageId' | 'name' | 'createdAt' | 'updatedAt'
>;

export const ProjectStages = {
  Proposal: 1,
  ActiveProject: 2,
  ServiceClient: 3,
  ProposalLost: 4,
};

export const ProjectStageColors = {
  [ProjectStages.Proposal]: '#FFB402',
  [ProjectStages.ActiveProject]: '#FF8080',
  [ProjectStages.ServiceClient]: '#77DBB0',
  [ProjectStages.ProposalLost]: '#B1B1B2',
};

export const useProjectStages = (params?: {
  filters?: ConditionInput[];
  limit?: number;
  sort?: SortingInput;
  options?: QueryHookOptions<GetProjectStages>;
}) => {
  const { data, fetchMore, variables, ...rest } = useQuery<GetProjectStages, QueryVariables>(
    GET_PROJECT_STAGE_QUERY,
    {
      ...(params?.options ?? {}),
      variables: {
        skipTotalRecords: true,
        params: {
          filters: params?.filters,
          sorting: params?.sort,
          limit: params?.limit,
        },
      },
    },
  );

  const projectStages = useMemo(
    () =>
      data?.getProjectStage.records
        .map((i) => ({
          ...i,
          label: i.name,
          value: i.id,
        }))
        .sort((a, b) => a.id - b.id) || [],
    [data?.getProjectStage.records],
  );

  const { loadMore, hasReachedEnd } = useLoadMore<GetProjectStages>({
    offset: projectStages.length,
    fetchMore,
    params: variables?.params!,
  });

  const totalRecords = data?.getProjectStage?.metadata?.totalRecords ?? 0;

  return {
    projectStages,
    totalRecords,
    loadMore,
    hasMore: totalRecords ? projectStages?.length < totalRecords : !hasReachedEnd,
    ...rest,
  };
};
